import { RegisterableHotkey, normalizeRegisterableHotkey } from '@tanstack/react-hotkeys';
import { isNil } from 'lodash';
import { TooltipPosition } from './types';

export const TOOLTIP_ATTRIBUTE = 'data-tooltip';
export const TOOLTIP_POSITION_ATTRIBUTE = 'data-tooltip-position';
export const TOOLTIP_HOTKEY_ATTRIBUTE = 'data-tooltip-hotkey';

export interface TooltipOptions {
  position?: TooltipPosition;
  /** Renders a key cap hint beside the tooltip text, when hints are enabled. */
  hotkey?: RegisterableHotkey | null;
  /**
   * Returns no attributes. Keycap-only tooltips have no text to leave out, so
   * this is how they turn off.
   */
  disabled?: boolean;
}

export interface TooltipAttributes {
  [TOOLTIP_ATTRIBUTE]?: string;
  [TOOLTIP_POSITION_ATTRIBUTE]?: TooltipPosition;
  [TOOLTIP_HOTKEY_ATTRIBUTE]?: string;
}

/**
 * Props that make an element a tooltip trigger for the app-wide `TooltipPresenter`:
 *
 *   <button {...getTooltipProps('Save', { hotkey: 'Mod+S' })} />
 *
 * Falsy text with no hotkey returns no attributes, so conditional tooltips can
 * pass `isReadOnly && label` directly.
 */
export function getTooltipProps(
  text: string | null | undefined | false,
  { position, hotkey, disabled = false }: TooltipOptions = {},
): TooltipAttributes {
  const hasContent = !!text || !isNil(hotkey);
  if (disabled || !hasContent) {
    return {};
  }

  return {
    [TOOLTIP_ATTRIBUTE]: text || '',
    [TOOLTIP_POSITION_ATTRIBUTE]: position,
    [TOOLTIP_HOTKEY_ATTRIBUTE]: isNil(hotkey) ? undefined : normalizeRegisterableHotkey(hotkey),
  };
}
